import type {
  CameraTrackSummary,
  MediaDeviceSummary,
  MediaSignals,
  VideoFrameProbe,
} from '@fraudeai/schema';
import { defineModule, type ModuleContext } from '../harness.js';
import { attempt, attemptAsync, delay, round, withTimeout } from '../util/safe.js';

type DecodingInfo = {
  supported: boolean;
  smooth: boolean;
  powerEfficient: boolean;
};

const VIRTUAL_LABELS: [RegExp, string][] = [
  [/obs|virtual camera/i, 'virtual-camera'],
  [/manycam/i, 'manycam'],
  [/snap camera|snapcam/i, 'snap-camera'],
  [/droidcam/i, 'droidcam'],
  [/iriun/i, 'iriun'],
  [/camtwist/i, 'camtwist'],
  [/xsplit/i, 'xsplit'],
  [/ndi/i, 'ndi'],
  [/splitcam/i, 'splitcam'],
  [/webcamoid/i, 'webcamoid'],
  [/v4l2loopback/i, 'v4l2loopback'],
  [/blackhole|vb-audio|voicemeeter/i, 'virtual-audio'],
];

const MEDIA_CAPABILITY_PROBES = {
  h264_720p: 'video/mp4; codecs="avc1.42E01E"',
  h265_720p: 'video/mp4; codecs="hvc1.1.6.L93.B0"',
  vp9_720p: 'video/webm; codecs="vp09.00.10.08"',
  av1_720p: 'video/mp4; codecs="av01.0.05M.08"',
};

function summarizeDevices(devices: MediaDeviceInfo[]): {
  counts: MediaDeviceSummary[];
  labelled: boolean;
  hints: string[];
} {
  const counts = new Map<string, number>();
  const hints = new Set<string>();
  let labelled = false;

  for (const device of devices) {
    counts.set(device.kind, (counts.get(device.kind) ?? 0) + 1);
    if (device.label) {
      labelled = true;
      for (const [pattern, hint] of VIRTUAL_LABELS) {
        if (pattern.test(device.label)) hints.add(`${device.kind}:${hint}`);
      }
    }
  }

  return {
    counts: [...counts.entries()]
      .map(([kind, count]) => ({ kind, count }))
      .sort((a, b) => a.kind.localeCompare(b.kind)),
    labelled,
    hints: [...hints].sort(),
  };
}

async function enumerate(): Promise<ReturnType<typeof summarizeDevices> | null> {
  return attemptAsync(async () => {
    if (!navigator.mediaDevices?.enumerateDevices) return null;
    return summarizeDevices(await navigator.mediaDevices.enumerateDevices());
  });
}

function cameraTrackSummary(track: MediaStreamTrack): CameraTrackSummary {
  const settings = attempt(() => track.getSettings?.()) ?? {};
  const s = settings as MediaTrackSettings & { resizeMode?: string };
  return {
    width: typeof s.width === 'number' ? s.width : null,
    height: typeof s.height === 'number' ? s.height : null,
    frameRate: typeof s.frameRate === 'number' ? s.frameRate : null,
    aspectRatio: typeof s.aspectRatio === 'number' ? round(s.aspectRatio, 4) : null,
    facingMode: typeof s.facingMode === 'string' ? s.facingMode : null,
    resizeMode: typeof s.resizeMode === 'string' ? s.resizeMode : null,
  };
}

function frameMean(video: HTMLVideoElement, canvas: HTMLCanvasElement): number | null {
  return attempt(() => {
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx || video.videoWidth === 0 || video.videoHeight === 0) return null;

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const pixels = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
    let sum = 0;
    for (let i = 0; i < pixels.length; i += 4) {
      sum += pixels[i] * 0.2126 + pixels[i + 1] * 0.7152 + pixels[i + 2] * 0.0722;
    }
    return sum / (pixels.length / 4);
  });
}

async function probeFrames(stream: MediaStream): Promise<VideoFrameProbe | null> {
  return attemptAsync(async () => {
    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.srcObject = stream;
    await video.play();

    const canvas = document.createElement('canvas');
    canvas.width = 16;
    canvas.height = 16;

    const samples: { at: number; mean: number }[] = [];
    const supportsRvfc = 'requestVideoFrameCallback' in HTMLVideoElement.prototype;

    for (let i = 0; i < 12; i++) {
      if (supportsRvfc) {
        await new Promise<void>((resolve) => {
          video.requestVideoFrameCallback(() => resolve());
        });
      } else {
        await delay(120);
      }
      const mean = frameMean(video, canvas);
      if (mean !== null) samples.push({ at: performance.now(), mean });
    }

    video.pause();
    video.srcObject = null;

    if (samples.length < 2) {
      return {
        supported: supportsRvfc,
        frames: samples.length,
        duplicateFrames: 0,
        avgIntervalMs: null,
        maxIntervalMs: null,
        meanPixelDelta: null,
        frozen: null,
      };
    }

    let duplicateFrames = 0;
    let deltaSum = 0;
    let intervalSum = 0;
    let maxInterval = 0;
    for (let i = 1; i < samples.length; i++) {
      const delta = Math.abs(samples[i].mean - samples[i - 1].mean);
      const interval = samples[i].at - samples[i - 1].at;
      if (delta < 0.25) duplicateFrames++;
      deltaSum += delta;
      intervalSum += interval;
      maxInterval = Math.max(maxInterval, interval);
    }

    const intervals = samples.length - 1;
    return {
      supported: supportsRvfc,
      frames: samples.length,
      duplicateFrames,
      avgIntervalMs: round(intervalSum / intervals, 1),
      maxIntervalMs: round(maxInterval, 1),
      meanPixelDelta: round(deltaSum / intervals, 3),
      frozen: duplicateFrames >= intervals - 1,
    };
  });
}

async function collectMediaCapabilities(): Promise<MediaSignals['mediaCapabilities']> {
  return attemptAsync(async () => {
    const capabilities = navigator.mediaCapabilities;
    if (!capabilities?.decodingInfo) return null;

    const out: NonNullable<MediaSignals['mediaCapabilities']> = {};
    for (const [name, contentType] of Object.entries(MEDIA_CAPABILITY_PROBES)) {
      const info = await attemptAsync(() =>
        capabilities.decodingInfo({
          type: 'file',
          video: {
            contentType,
            width: 1280,
            height: 720,
            bitrate: 2_000_000,
            framerate: 30,
          },
        }),
      );
      out[name] = info
        ? {
            supported: (info as DecodingInfo).supported,
            smooth: (info as DecodingInfo).smooth,
            powerEfficient: (info as DecodingInfo).powerEfficient,
          }
        : null;
    }
    return out;
  });
}

export const mediaModule = defineModule({
  id: 'media',
  phase: 1,
  timeoutMs: 8000,
  async run(ctx: ModuleContext): Promise<MediaSignals> {
    const before = await enumerate();
    let after = before;
    let cameraTrack: CameraTrackSummary | null = null;
    let videoFrame: VideoFrameProbe | null = null;

    if (ctx.options.requestMediaProbe && navigator.mediaDevices?.getUserMedia) {
      const stream = await attemptAsync(() =>
        withTimeout(
          navigator.mediaDevices.getUserMedia({ video: true, audio: false }),
          7000,
          'media-permission',
        ),
      );
      if (stream) {
        const [track] = stream.getVideoTracks();
        if (track) {
          cameraTrack = cameraTrackSummary(track);
          videoFrame = await probeFrames(stream);
        }
        after = (await enumerate()) ?? before;
        for (const track of stream.getTracks()) track.stop();
      }
    }

    const hints = new Set([...(before?.hints ?? []), ...(after?.hints ?? [])]);

    return {
      supported: !!navigator.mediaDevices,
      permissionRequested: ctx.options.requestMediaProbe === true,
      devicesBefore: before?.counts ?? null,
      devicesAfter: after?.counts ?? null,
      labelledBefore: before?.labelled ?? null,
      labelledAfter: after?.labelled ?? null,
      virtualDeviceHints: hints.size > 0 ? [...hints].sort() : null,
      cameraTrack,
      videoFrame,
      displayCaptureSupported: attempt(() => !!navigator.mediaDevices?.getDisplayMedia),
      webCodecs: {
        videoEncoder: 'VideoEncoder' in globalThis,
        videoDecoder: 'VideoDecoder' in globalThis,
        audioEncoder: 'AudioEncoder' in globalThis,
        audioDecoder: 'AudioDecoder' in globalThis,
      },
      mediaCapabilities: await collectMediaCapabilities(),
    };
  },
});
